import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CreditCard, Plus, Trash2, Wallet } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const PaymentMethods = ({
  paymentMethods,
  setPaymentMethods,
  showAddCardModal,
  setShowAddCardModal,
  showAddWalletModal,
  setShowAddWalletModal,
  showDeleteConfirm,
  setShowDeleteConfirm,
  cardToDelete,
  setCardToDelete,
  cardForm,
  setCardForm,
  walletForm,
  setWalletForm
}) => {

  const handleAddCard = () => {
    // Validate card form
    if (!cardForm.cardNumber || !cardForm.cardName || !cardForm.expiryDate || !cardForm.cvv) {
      toast({
        title: 'Error',
        description: 'Please fill in all card details',
        variant: 'destructive',
      });
      return;
    }

    // Basic card number validation (16 digits)
    const cleanCardNumber = cardForm.cardNumber.replace(/\s/g, '');
    if (cleanCardNumber.length !== 16 || !/^\d+$/.test(cleanCardNumber)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid 16-digit card number',
        variant: 'destructive',
      });
      return;
    }

    // Add new payment method
    const newCard = {
      id: Date.now().toString(),
      last4: cleanCardNumber.slice(-4),
      brand: getCardBrand(cleanCardNumber),
      cardName: cardForm.cardName,
      expiryDate: cardForm.expiryDate,
      isDefault: paymentMethods.length === 0,
    };

    const updatedMethods = [...paymentMethods, newCard];
    setPaymentMethods(updatedMethods);
    localStorage.setItem('payment_methods', JSON.stringify(updatedMethods));

    // Reset form and close modal
    setCardForm({
      cardNumber: '',
      cardName: '',
      expiryDate: '',
      cvv: '',
    });
    setShowAddCardModal(false);

    toast({
      title: 'Card added',
      description: 'Your payment method has been added successfully',
    });
  };

  const getCardBrand = (cardNumber) => {
    const firstDigit = cardNumber[0];
    if (firstDigit === '4') return 'Visa';
    if (firstDigit === '5') return 'Mastercard';
    if (firstDigit === '3') return 'Amex';
    return 'Card';
  };

  const handleDeleteCard = (cardId) => {
    setCardToDelete(cardId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCard = () => {
    if (!cardToDelete) return;

    const updatedMethods = paymentMethods.filter(card => card.id !== cardToDelete);
    setPaymentMethods(updatedMethods);
    localStorage.setItem('payment_methods', JSON.stringify(updatedMethods));

    toast({
      title: 'Card removed',
      description: 'Payment method has been removed',
    });

    setShowDeleteConfirm(false);
    setCardToDelete(null);
  };

  const handleAddWallet = () => {
    // Validate wallet form
    if (!walletForm.walletType) {
      toast({
        title: 'Error',
        description: 'Please select a wallet type',
        variant: 'destructive',
      });
      return;
    }

    if (walletForm.walletType === 'paypal' && !walletForm.email) {
      toast({
        title: 'Error',
        description: 'Please enter your PayPal email',
        variant: 'destructive',
      });
      return;
    }

    if (['apple_pay', 'google_pay'].includes(walletForm.walletType) && !walletForm.phoneNumber) {
      toast({
        title: 'Error',
        description: 'Please enter your phone number',
        variant: 'destructive',
      });
      return;
    }

    // Add new wallet
    const newWallet = {
      id: Date.now().toString(),
      type: 'wallet',
      walletType: walletForm.walletType,
      email: walletForm.email,
      phoneNumber: walletForm.phoneNumber,
      isDefault: paymentMethods.length === 0,
    };

    const updatedMethods = [...paymentMethods, newWallet];
    setPaymentMethods(updatedMethods);
    localStorage.setItem('payment_methods', JSON.stringify(updatedMethods));

    // Reset form and close modal
    setWalletForm({
      walletType: 'paypal',
      email: '',
      phoneNumber: '',
    });
    setShowAddWalletModal(false);

    toast({
      title: 'Wallet added',
      description: 'Your wallet has been added successfully',
    });
  };

  const getWalletIcon = (walletType) => {
    const icons = {
      paypal: '💳',
      apple_pay: '🍎',
      google_pay: '📱',
      venmo: '💵',
    };
    return icons[walletType] || '💰';
  };

  const getWalletName = (walletType) => {
    const names = {
      paypal: 'PayPal',
      apple_pay: 'Apple Pay',
      google_pay: 'Google Pay',
      venmo: 'Venmo',
    };
    return names[walletType] || 'Wallet';
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const groups = cleaned.match(/.{1,4}/g);
    return groups ? groups.join(' ') : cleaned;
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 16) {
      setCardForm({ ...cardForm, cardNumber: formatted });
    }
  };

  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2, 4);
    }
    setCardForm({ ...cardForm, expiryDate: value });
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowAddCardModal(true)}
              className="flex items-center gap-2"
            >
              <CreditCard className="h-4 w-4" />
              Add Card
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowAddWalletModal(true)}
              className="flex items-center gap-2"
            >
              <Wallet className="h-4 w-4" />
              Add Wallet
            </Button>
          </div>
        </div>
        
        {paymentMethods.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No payment methods added yet
          </div>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div 
                key={method.id} 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {method.type === 'wallet' ? (
                    <>
                      <div className="w-12 h-8 bg-gradient-to-r from-green-600 to-teal-600 rounded flex items-center justify-center text-2xl">
                        {getWalletIcon(method.walletType)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {getWalletName(method.walletType)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {method.email || method.phoneNumber}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded flex items-center justify-center text-white text-xs font-bold">
                        {method.brand.slice(0, 4)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {method.brand} •••• {method.last4}
                        </p>
                        <p className="text-sm text-gray-600">
                          {method.cardName} • Expires {method.expiryDate}
                        </p>
                      </div>
                    </>
                  )}
                  {method.isDefault && (
                    <span className="ml-2 px-2 py-1 bg-pink-100 text-pink-700 text-xs font-medium rounded">
                      Default
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteCard(method.id);
                  }}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Add Card Modal */}
      <Dialog open={showAddCardModal} onOpenChange={setShowAddCardModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="cardNumber">Card Number</Label>
              <Input
                id="cardNumber"
                placeholder="1234 5678 9012 3456"
                value={cardForm.cardNumber}
                onChange={handleCardNumberChange}
                maxLength={19}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cardName">Cardholder Name</Label>
              <Input
                id="cardName"
                placeholder="John Doe"
                value={cardForm.cardName}
                onChange={(e) => setCardForm({ ...cardForm, cardName: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expiryDate">Expiry Date</Label>
                <Input
                  id="expiryDate"
                  placeholder="MM/YY"
                  value={cardForm.expiryDate}
                  onChange={handleExpiryChange}
                  maxLength={5}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cvv">CVV</Label>
                <Input
                  id="cvv"
                  placeholder="123"
                  value={cardForm.cvv}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                    setCardForm({ ...cardForm, cvv: value });
                  }}
                  maxLength={3}
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Your payment information is securely stored and encrypted. We never share your card details.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddCardModal(false);
                  setCardForm({ cardNumber: '', cardName: '', expiryDate: '', cvv: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddCard}
                className="flex-1 bg-pink-600 hover:bg-pink-700"
              >
                Add Card
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Wallet Modal */}
      <Dialog open={showAddWalletModal} onOpenChange={setShowAddWalletModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Wallet</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="walletType">Wallet Type</Label>
              <select
                id="walletType"
                value={walletForm.walletType}
                onChange={(e) => setWalletForm({ ...walletForm, walletType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="paypal">PayPal</option>
                <option value="apple_pay">Apple Pay</option>
                <option value="google_pay">Google Pay</option>
                <option value="venmo">Venmo</option>
              </select>
            </div>

            {walletForm.walletType === 'paypal' && (
              <div className="space-y-2">
                <Label htmlFor="walletEmail">PayPal Email</Label>
                <Input
                  id="walletEmail"
                  type="email"
                  placeholder="your@email.com"
                  value={walletForm.email}
                  onChange={(e) => setWalletForm({ ...walletForm, email: e.target.value })}
                />
              </div>
            )}

            {['apple_pay', 'google_pay'].includes(walletForm.walletType) && (
              <div className="space-y-2">
                <Label htmlFor="walletPhone">Phone Number</Label>
                <Input
                  id="walletPhone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={walletForm.phoneNumber}
                  onChange={(e) => setWalletForm({ ...walletForm, phoneNumber: e.target.value })}
                />
              </div>
            )}

            {walletForm.walletType === 'venmo' && (
              <div className="space-y-2">
                <Label htmlFor="venmoPhone">Venmo Phone Number</Label>
                <Input
                  id="venmoPhone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={walletForm.phoneNumber}
                  onChange={(e) => setWalletForm({ ...walletForm, phoneNumber: e.target.value })}
                />
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> Your wallet will be linked to your Tekstar account. You'll be redirected to complete the authentication when making a payment.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddWalletModal(false);
                  setWalletForm({ walletType: 'paypal', email: '', phoneNumber: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddWallet}
                className="flex-1 bg-pink-600 hover:bg-pink-700"
              >
                Add Wallet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove Payment Method</DialogTitle>
          </DialogHeader>
          
          <div className="pt-4">
            <p className="text-gray-600 mb-6">
              Are you sure you want to remove this payment method? This action cannot be undone.
            </p>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setCardToDelete(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteCard}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Remove Card
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentMethods;